import React, { useMemo, useState } from "react";
import { arrayOf, bool, func, node, number, oneOfType, shape, string } from "prop-types";
import { useTranslation } from "react-i18next";
import { Grid } from "@material-ui/core";
import Link from "@material-ui/core/Link";
import { WorkTypeCheckbox } from "./WorkTypeCheckbox";
import { ExpandLess, ExpandMore } from "@material-ui/icons";
import { Checkbox } from "@components";
import classnames from "classnames";

export const SettingsCheckboxList = ({
  availableItems = [],
  selectedIds = [],
  onSelectItems,
  hideList,
  maxVisible = 3,
  showOnlySelected,
  blacklistedItems = [],
  editedItemName,
  showPercentageTooltip,
  title,
  selectAllTitle,
  emptyLabel,
  className,
  showSelectAllOnList,
  allowSelectAll,
  disabled,
  headerAction,
}) => {
  const { t } = useTranslation();
  
  const [ expand, setExpand ] = useState(false);

  const filteredList = useMemo(() => (
    showOnlySelected
      ? (availableItems || []).filter((item) => selectedIds?.includes(item.id))
      : availableItems
  ), [showOnlySelected, availableItems, selectedIds]);

  const allowedItemList = useMemo(() => (
    (availableItems || []).filter((item) => !blacklistedItems.find(b => b.id === item.id))
  ), [showOnlySelected, availableItems, selectedIds]);

  const allowHidingList = useMemo(() => (
    Boolean(hideList && filteredList?.length > maxVisible)
  ), [hideList, filteredList, maxVisible]);

  const handleShowMore = () => setExpand(!expand);
  
  const handleSelectItem = (id) => () => {
    onSelectItems?.(selectedIds.includes(id) ? selectedIds.filter((s) => s !== id) : [...selectedIds, id]);
  };
  
  const handleSelectAllItems = () => {
    onSelectItems?.(
      !allowedItemList?.length || selectedIds.length === allowedItemList?.length
        ? []
        : [...allowedItemList.map(s => s.id)]
    );
  };
  
  return (
    <Grid
      item container
      direction="column"
      className={classnames(className)}
    >
      <Grid
        item container
        alignItems="center"
        justifyContent="space-between"
        wrap="nowrap"
        className="mb-2 h-20 gap-2"
      >
        {
          allowSelectAll && !showSelectAllOnList
            ? <Checkbox
              name="selectAll"
              aria-label={selectAllTitle || "select all"}
              checked={!filteredList?.length || selectedIds.length >= filteredList?.length}
              onChange={handleSelectAllItems}
              indeterminate={Boolean(
                selectedIds.length && filteredList?.length && selectedIds.length !== filteredList?.length
              )}
              className="m-0 ml-3 -mt-0-5"
              label={title}
              disabled={disabled}
            />
            : <p>
              {title}
            </p>
        }
        {headerAction}
      </Grid>
      <Grid
        item
        container
        direction="column"
        className="bg-primary p-3 rounded gap-2"
      >
        {
          allowSelectAll && showSelectAllOnList &&
          <Checkbox
            name="selectAll"
            aria-label={selectAllTitle || "select all"}
            checked={!filteredList?.length || selectedIds.length >= filteredList?.length}
            onChange={handleSelectAllItems}
            indeterminate={Boolean(
              selectedIds.length && filteredList?.length && selectedIds.length !== filteredList?.length
            )}
            labelClassName="font-bold"
            label={selectAllTitle || title}
            disabled={disabled}
          />
        }
        {
          filteredList
            ?.slice(0, allowHidingList && !expand ? maxVisible : undefined)
            .map(({ id, name, percentageWorkTypes }) => (
              <WorkTypeCheckbox
                key={id}
                id={id}
                name={name || t("common.unnamed")}
                percentageWorkTypes={percentageWorkTypes}
                editedWorkTypeName={editedItemName}
                checked={selectedIds.indexOf(id) >= 0}
                onChange={handleSelectItem(id)}
                usedInSections={blacklistedItems?.find((bw) => id === bw.id)?.sections}
                showPercentageWorkTypesTooltip={showPercentageTooltip}
                disabled={disabled}
              />
            ))
        }
        {
          allowHidingList &&
          <Link
            className="text-sm font-bold cursor-pointer"
            onClick={handleShowMore}
          >
            <Grid item container alignItems="center">
              {expand ? <ExpandLess className="text-2xl" /> : <ExpandMore className="text-2xl" />}
              {expand ? t("common.show_less") : t("common.show_more")}
            </Grid>
          </Link>
        }
        {
          !showOnlySelected && !filteredList?.length &&
          <p className="semi-transparent">
            {emptyLabel}
          </p>
        }
      </Grid>
    </Grid>
  );
};

SettingsCheckboxList.propTypes = {
  availableItems: arrayOf(shape({
    id: oneOfType([number, string]).isRequired,
    name: string,
    percentageWorkTypes: arrayOf(shape({
      id: oneOfType([number, string]).isRequired,
      name: string,
    }))
  })),
  selectedIds: arrayOf(oneOfType([number, string])),
  onSelectItems: func.isRequired,
  hideList: bool,
  maxVisible: number,
  showOnlySelected: bool,
  blacklistedItems: arrayOf(shape({
    id: oneOfType([number, string]).isRequired,
    sections: arrayOf(string).isRequired
  })),
  editedItemName: string,
  showPercentageTooltip: bool,
  title: string,
  selectAllTitle: string,
  emptyLabel: string,
  className: string,
  showSelectAllOnList: bool,
  allowSelectAll: bool,
  disabled: bool,
  headerAction: node,
};

export default SettingsCheckboxList;
